/**
 * CandidatePhotoNudge — V1.8.6
 *
 * Slim banner shown on the candidate dashboard when the user has no
 * profile photo AND hasn't dismissed the nudge in the last 30 days.
 *
 * Rationale: recruiters shortlist faster when a face is attached. The
 * nudge is one-tap dismissable and a soft nudge — never blocking.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Camera, ArrowRight, X } from "@phosphor-icons/react";

const STORAGE_KEY = "candidate_photo_nudge_dismissed_at";
const SNOOZE_DAYS = 30;

export default function CandidatePhotoNudge() {
    const { user } = useAuth();
    const [dismissed, setDismissed] = React.useState(false);

    React.useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const at = Number(raw);
            if (!Number.isFinite(at)) return;
            const days = (Date.now() - at) / (1000 * 60 * 60 * 24);
            if (days < SNOOZE_DAYS) setDismissed(true);
        } catch { /* SSR / private-mode: fall through */ }
    }, []);

    if (!user || user.avatar_url || dismissed) return null;

    const onDismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            data-testid="candidate-photo-nudge"
            className="mb-4 rounded-2xl bg-gradient-to-r from-cobalt/10 via-white to-emerald-50 border border-cobalt/20 p-4 sm:p-5 flex items-center gap-4"
        >
            <div className="hidden sm:flex w-12 h-12 rounded-full bg-cobalt text-white items-center justify-center shrink-0 shadow">
                <Camera size={22} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-0.5">
                    3× more shortlists
                </p>
                <p className="text-sm font-bold text-ink leading-snug">
                    Add a friendly profile photo — recruiters remember faces.
                </p>
                <p className="text-[11px] text-ink/60 mt-0.5 hidden sm:block">
                    Takes 10 seconds. Only appears inside your full profile — never in the search list.
                </p>
            </div>
            <Link
                to="/candidate/profile#photo"
                data-testid="candidate-photo-nudge-cta"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-white text-[11px] font-black uppercase tracking-wider hover:opacity-90 shrink-0"
            >
                Add photo
                <ArrowRight size={11} weight="bold" />
            </Link>
            <button
                type="button"
                onClick={onDismiss}
                data-testid="candidate-photo-nudge-dismiss"
                aria-label="Dismiss for 30 days"
                className="w-7 h-7 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-ink/50 hover:text-ink shrink-0 ring-1 ring-ink/10"
            >
                <X size={12} weight="bold" />
            </button>
        </div>
    );
}
